import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ExternalLink, Sparkles, AlertTriangle, MessageSquarePlus, ChevronRight } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const MODES = [
  { id: "quick",         label: "Quick Q&A",      blurb: "Short educational answer." },
  { id: "pathway",       label: "Build a Pathway", blurb: "Guided intake → preliminary plan." },
  { id: "compare",       label: "Compare Schools", blurb: "Side-by-side institution or major comparison." },
  { id: "review",        label: "Review My Record", blurb: "Analyze grades / withdrawals / situation." },
  { id: "parent_brief",  label: "Parent Brief",   blurb: "Rewrite in parent-facing language." },
  { id: "adviser_prep",  label: "Adviser Prep",   blurb: "Questions for your institutional adviser." },
  { id: "session_prep",  label: "Session Prep",   blurb: "Pre-consultation brief for a BV adviser." },
];

function MarkdownLite({ text }) {
  // Renders bold **x**, italic *x*, tables and paragraphs. No dependencies — we keep it small.
  if (!text) return null;
  const blocks = text.split(/\n{2,}/);
  return (
    <div className="space-y-3 text-[15px] text-bone leading-relaxed">
      {blocks.map((blk, i) => {
        // Detect a simple markdown table (starts with | ... | )
        const lines = blk.split("\n");
        if (lines.length >= 2 && lines[0].trim().startsWith("|") && lines[1].includes("---")) {
          const rows = lines.filter((l) => l.trim().startsWith("|"));
          const headers = rows[0].split("|").map((c) => c.trim()).filter(Boolean);
          const dataRows = rows.slice(2).map((r) => r.split("|").map((c) => c.trim()).filter((c) => c.length > 0 || true));
          return (
            <div key={i} className="overflow-x-auto border border-bone-dim/25">
              <table className="w-full text-[13px]">
                <thead className="bg-bone-dim/10 text-brass-soft">
                  <tr>{headers.map((h, k) => <th key={k} className="text-left px-3 py-2 uppercase tracking-[0.14em] text-[10px] font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {dataRows.map((r, rk) => (
                    <tr key={rk} className="border-t border-bone-dim/15">
                      {headers.map((_, ck) => <td key={ck} className="px-3 py-2 align-top text-bone-dim">{r[ck + 1] ?? ""}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        // Regular paragraph — inline bold + italic
        const html = blk
          .replace(/\*\*([^*]+)\*\*/g, "<strong class='text-bone'>$1</strong>")
          .replace(/\*([^*]+)\*/g, "<em class='italic text-brass-soft'>$1</em>")
          .replace(/\n/g, "<br/>");
        return <p key={i} dangerouslySetInnerHTML={{ __html: html }} />;
      })}
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1.5 py-2" data-testid="neo-typing">
      <span className="w-1.5 h-1.5 bg-brass rounded-full animate-pulse" style={{ animationDelay: "0ms" }} />
      <span className="w-1.5 h-1.5 bg-brass rounded-full animate-pulse" style={{ animationDelay: "180ms" }} />
      <span className="w-1.5 h-1.5 bg-brass rounded-full animate-pulse" style={{ animationDelay: "360ms" }} />
    </div>
  );
}

export default function Neo() {
  const { user } = useAuth();
  const [mode, setMode] = useState("quick");
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [savingPathway, setSavingPathway] = useState(false);
  const threadRef = useRef(null);

  const scrollToBottom = () => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  };

  const bootSession = useCallback(async (m) => {
    try {
      const { data } = await api.post("/neo/session", { mode: m });
      setSession(data);
      setMessages(data.messages || []);
    } catch (e) {
      toast.error("Could not start NEO. Try again.");
    }
  }, []);

  // Boot a new session on mount
  useEffect(() => { bootSession("quick"); }, [bootSession]);

  useEffect(() => { scrollToBottom(); }, [messages]);

  // Load history for authed users
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/neo/sessions");
        setHistory(data || []);
      } catch (_e) { /* silent */ }
    })();
  }, [user, session?.session_id]);

  const openHistory = async (sid) => {
    try {
      const { data } = await api.get(`/neo/sessions/${sid}`);
      setSession(data);
      setMessages(data.messages || []);
      setMode(data.mode || "quick");
    } catch (_e) {
      toast.error("Could not load session.");
    }
  };

  const newSession = () => bootSession(mode);

  const send = async () => {
    if (!input.trim() || sending || !session) return;
    const text = input.trim();
    const now = new Date().toISOString();
    setMessages((prev) => [...prev, { role: "user", text, timestamp: now }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/neo/message", {
        session_id: session.session_id,
        mode,
        text,
      });
      setMessages((prev) => [...prev, {
        role: "assistant",
        text: data.text,
        actions: data.actions,
        timestamp: data.timestamp,
      }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not reach NEO. Try again.";
      const status = e?.response?.status;
      if (status === 429) {
        toast.error("Free daily limit reached. Sign in for unlimited NEO.");
      } else {
        toast.error(detail);
      }
      setMessages((prev) => [...prev, {
        role: "assistant",
        text: status === 429
          ? "You've reached the daily free limit for NEO. Sign in for unlimited access."
          : "I couldn't reach my knowledge base for that response. Try again — or rephrase your question.",
        actions: { suggested_followups: [], sources: [], escalate: false, escalate_reason: "", save_pathway: null },
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
    }
  };

  const savePathway = async (pathway) => {
    if (!user) {
      toast.info("Sign in to save this pathway to your dashboard.");
      return;
    }
    setSavingPathway(true);
    try {
      const { data } = await api.post("/neo/save-pathway", pathway);
      toast.success(`Saved ${data.created} milestones to your dashboard.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save pathway.");
    } finally {
      setSavingPathway(false);
    }
  };

  const sendFollowup = (text) => {
    setInput(text);
    // small delay so state updates before send
    setTimeout(send, 30);
  };

  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  const rightRailSources = lastAssistant?.actions?.sources || [];
  const rightRailFollowups = lastAssistant?.actions?.suggested_followups || [];

  return (
    <Layout>
      <SEO
        title="NEO — AI Transfer Navigator | Beyond Valencia"
        description="NEO is a Florida-grounded AI transfer navigator. Ask questions, build preliminary pathways, and prepare for consultations — all backed by the Common Prerequisites Manual and Florida articulation policy."
        canonical="/neo"
        noindex
      />
      <section className="bg-ivy min-h-[calc(100vh-4rem)] text-bone" data-testid="neo-page">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-[260px_1fr_300px] gap-0">

          {/* LEFT RAIL — modes + session history */}
          <aside className="hidden lg:block border-r border-bone-dim/10 bg-ivy-dark/30 p-5 min-h-[calc(100vh-4rem)]" data-testid="neo-left-rail">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 bg-brass text-ink flex items-center justify-center font-serif font-semibold">BV</div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft">NEO</div>
                <div className="text-sm font-medium">Transfer Navigator</div>
              </div>
            </div>

            <div className="text-[10px] uppercase tracking-[0.22em] text-bone-dim/60 mb-3">Mode</div>
            <div className="space-y-1.5 mb-8" data-testid="neo-mode-selector">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  className={`w-full text-left px-3 py-2 transition-colors text-sm border-l-2 ${
                    mode === m.id
                      ? "border-brass bg-brass/10 text-bone"
                      : "border-transparent text-bone-dim hover:text-bone hover:bg-bone-dim/5"
                  }`}
                  data-testid={`neo-mode-${m.id}`}
                >
                  <div className="font-medium">{m.label}</div>
                  <div className="text-[11px] text-bone-dim/60 mt-0.5">{m.blurb}</div>
                </button>
              ))}
            </div>

            <button
              onClick={newSession}
              className="w-full flex items-center justify-center gap-2 bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[10px] font-semibold py-2.5 transition-colors mb-8"
              data-testid="neo-new-session"
            >
              <MessageSquarePlus className="h-3.5 w-3.5" /> New conversation
            </button>

            {user && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-bone-dim/60 mb-3">Your history</div>
                <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1" data-testid="neo-history-list">
                  {history.length === 0 ? (
                    <p className="text-[11px] text-bone-dim/50 italic">No past sessions yet.</p>
                  ) : (
                    history.map((s) => (
                      <button
                        key={s.session_id}
                        onClick={() => openHistory(s.session_id)}
                        className={`w-full text-left px-2.5 py-2 hover:bg-bone-dim/5 transition-colors border-l-2 ${
                          session?.session_id === s.session_id ? "border-brass bg-brass/5" : "border-transparent"
                        }`}
                      >
                        <div className="text-[10px] uppercase tracking-[0.18em] text-brass-soft">{s.mode.replace(/_/g, " ")}</div>
                        <div className="text-[12px] text-bone-dim leading-tight mt-1 line-clamp-2">{s.preview}</div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </aside>

          {/* MAIN THREAD */}
          <main className="flex flex-col min-h-[calc(100vh-4rem)]" data-testid="neo-main">
            <div className="border-b border-bone-dim/10 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft">Mode</div>
                <div className="font-serif text-xl text-bone tracking-tight" data-testid="neo-current-mode">
                  {MODES.find((m) => m.id === mode)?.label}
                </div>
              </div>
              {!user && (
                <Link to="/login" className="text-[11px] uppercase tracking-[0.18em] text-brass hover:text-brass-soft transition-colors">
                  Sign in for unlimited →
                </Link>
              )}
            </div>

            <div ref={threadRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-6" data-testid="neo-thread">
              {messages.map((m, i) => (
                <MessageBubble
                  key={i}
                  message={m}
                  onFollowup={sendFollowup}
                  onSavePathway={savePathway}
                  savingPathway={savingPathway}
                />
              ))}
              {sending && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-brass text-ink flex items-center justify-center font-serif font-semibold text-xs flex-shrink-0">BV</div>
                  <div className="bg-ivy-dark/60 border-l-2 border-brass px-4 py-3"><TypingIndicator /></div>
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="border-t border-bone-dim/10 px-6 py-4">
              <div className="flex items-end gap-3">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                  }}
                  rows={2}
                  placeholder={`Ask NEO in ${MODES.find((m) => m.id === mode)?.label.toLowerCase()} mode… (Shift+Enter for newline)`}
                  className="flex-1 bg-ivy-dark/50 border border-bone-dim/20 focus:border-brass px-4 py-3 text-[15px] text-bone placeholder-bone-dim/40 outline-none resize-none transition-colors"
                  data-testid="neo-composer"
                  disabled={sending}
                />
                <button
                  onClick={send}
                  disabled={sending || !input.trim()}
                  className="bg-brass hover:bg-brass-soft disabled:opacity-40 disabled:cursor-not-allowed text-ink uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-5 transition-colors flex items-center gap-1.5"
                  data-testid="neo-send"
                >
                  Send <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="text-[10px] text-bone-dim/50 mt-2 uppercase tracking-[0.18em]">
                NEO can be inaccurate. Verify major-specific policy with the Common Prerequisites Manual before any academic decision.
              </p>
            </div>
          </main>

          {/* RIGHT RAIL — sources + suggested actions */}
          <aside className="hidden lg:block border-l border-bone-dim/10 bg-ivy-dark/30 p-5 min-h-[calc(100vh-4rem)]" data-testid="neo-right-rail">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft mb-3">Suggested follow-ups</div>
            {rightRailFollowups.length === 0 ? (
              <p className="text-[11px] text-bone-dim/50 italic mb-6">NEO will surface suggested questions here.</p>
            ) : (
              <div className="space-y-2 mb-8" data-testid="neo-followups-rail">
                {rightRailFollowups.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendFollowup(q)}
                    className="block w-full text-left border border-bone-dim/20 hover:border-brass/60 hover:bg-brass/5 px-3 py-2 text-[12px] text-bone-dim hover:text-bone transition-colors"
                  >
                    <ChevronRight className="h-3 w-3 inline mr-1 text-brass" />
                    {q}
                  </button>
                ))}
              </div>
            )}

            <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft mb-3">Sources</div>
            {rightRailSources.length === 0 ? (
              <p className="text-[11px] text-bone-dim/50 italic">Official-source citations will appear here when NEO uses them.</p>
            ) : (
              <div className="space-y-2" data-testid="neo-sources-rail">
                {rightRailSources.map((s, i) => (
                  s.url ? (
                    <a key={i} href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="block text-[12px] text-brass hover:text-brass-soft border-b border-brass/40 hover:border-brass pb-1 transition-colors">
                      <ExternalLink className="h-3 w-3 inline mr-1" />{s.title}
                    </a>
                  ) : (
                    <div key={i} className="text-[12px] text-bone-dim border-b border-bone-dim/20 pb-1">{s.title}</div>
                  )
                ))}
              </div>
            )}
          </aside>
        </div>
      </section>
    </Layout>
  );
}

function MessageBubble({ message, onFollowup, onSavePathway, savingPathway }) {
  const isUser = message.role === "user";
  if (isUser) {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex justify-end">
        <div className="max-w-[75%] bg-bone/10 border border-bone-dim/20 px-4 py-3 text-[15px] text-bone leading-relaxed" data-testid="neo-user-msg">
          {message.text}
        </div>
      </motion.div>
    );
  }
  const actions = message.actions || {};
  const pw = actions.save_pathway;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-start gap-3" data-testid="neo-assistant-msg">
      <div className="w-8 h-8 bg-brass text-ink flex items-center justify-center font-serif font-semibold text-xs flex-shrink-0">BV</div>
      <div className="flex-1 space-y-3">
        <div className="bg-ivy-dark/60 border-l-2 border-brass px-4 py-3">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">NEO</span>
            <Sparkles className="h-3 w-3 text-brass/70" />
          </div>
          <MarkdownLite text={message.text} />
        </div>

        {/* Suggested followups as inline chips */}
        {actions.suggested_followups?.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="neo-followup-chips">
            {actions.suggested_followups.slice(0, 4).map((q, i) => (
              <button
                key={i}
                onClick={() => onFollowup(q)}
                className="border border-brass/40 hover:border-brass hover:bg-brass/10 text-bone-dim hover:text-bone px-3 py-1.5 text-[12px] transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {/* Save-pathway CTA */}
        {pw && pw.items?.length > 0 && (
          <div className="border border-brass/40 bg-brass/5 p-4" data-testid="neo-pathway-card">
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft">Preliminary pathway</div>
                <div className="font-serif text-lg text-bone tracking-tight mt-1">{pw.title}</div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-bone-dim/70">{pw.items.length} milestones</span>
            </div>
            <ol className="border-l border-brass/40 pl-4 space-y-2.5 text-[13px] mb-4">
              {pw.items.slice(0, 6).map((it, i) => (
                <li key={i}>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-brass">{it.semester_label}</div>
                  <div className="text-bone leading-snug">{it.milestone_label}</div>
                </li>
              ))}
              {pw.items.length > 6 && <li className="text-[11px] text-bone-dim/60 italic">+ {pw.items.length - 6} more</li>}
            </ol>
            <button
              onClick={() => onSavePathway(pw)}
              disabled={savingPathway}
              className="w-full sm:w-auto bg-brass hover:bg-brass-soft disabled:opacity-50 text-ink uppercase tracking-[0.18em] text-[11px] font-semibold py-2.5 px-5 transition-colors inline-flex items-center gap-2"
              data-testid="neo-save-pathway"
            >
              {savingPathway ? "Saving…" : "Save this pathway →"}
            </button>
          </div>
        )}

        {/* Escalation callout */}
        {actions.escalate && (
          <div className="border-l-4 border-crimson bg-crimson/10 px-4 py-3" data-testid="neo-escalation">
            <div className="flex items-baseline gap-2 mb-1">
              <AlertTriangle className="h-4 w-4 text-crimson flex-shrink-0" />
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-crimson">Human strategist recommended</div>
            </div>
            {actions.escalate_reason && (
              <p className="text-[13px] text-bone leading-relaxed mb-3">{actions.escalate_reason}</p>
            )}
            <div className="flex flex-wrap gap-3">
              <Link to="/transfer-audit" className="bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[10px] font-semibold py-2 px-4 transition-colors">
                Schedule Transfer Audit — $249
              </Link>
              <Link to="/contact" className="border border-bone-dim/40 hover:border-bone text-bone uppercase tracking-[0.18em] text-[10px] font-semibold py-2 px-4 transition-colors">
                Book a Strategy Session
              </Link>
            </div>
          </div>
        )}

        {/* Inline source footnotes */}
        {actions.sources?.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1" data-testid="neo-sources-inline">
            {actions.sources.map((s, i) => (
              s.url ? (
                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="text-[11px] text-brass hover:text-brass-soft border-b border-brass/50 hover:border-brass transition-colors">
                  <ExternalLink className="h-2.5 w-2.5 inline mr-0.5" />{s.title}
                </a>
              ) : (
                <span key={i} className="text-[11px] text-bone-dim border-b border-bone-dim/30">{s.title}</span>
              )
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
